import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar/Navbar";
import Footer from "../components/Footer/Footer";
import { useTheme } from "../context/ThemeContext";
import { blogPosts } from "../data/blogData";
import { convertMarkdownToHTML } from "../utils/markdown";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const { currentTheme } = useTheme();
  const [content, setContent] = useState<string>("");
  const [loading, setLoading] = useState(true);

  const blog = blogPosts.find(post => post.slug === slug);

  useEffect(() => {
    if (blog) {
      // Import the markdown file
      import(`../data/blogs/${blog.markdownFile}`)
        .then(module => {
          // Fetch the raw markdown content
          fetch(module.default)
            .then(response => response.text())
            .then(text => {
              setContent(text);
              setLoading(false);
            });
        })
        .catch(error => {
          console.error("Error loading markdown:", error);
          // Fallback: try to fetch directly
          fetch(`/src/data/blogs/${blog.markdownFile}`)
            .then(response => response.text())
            .then(text => {
              setContent(text);
              setLoading(false);
            })
            .catch(() => {
              setContent("# Error loading blog content");
              setLoading(false);
            });
        });
    }
  }, [blog]);

  if (!blog) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-800 mb-4">Blog Post Not Found</h1>
            <Link to="/blog" className="text-purple-600 hover:text-purple-700 font-semibold">
              ← Back to Blog
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{blog.title} | Mukesh Rathore</title>
        <meta name="description" content={blog.excerpt} />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.excerpt} />
        <meta property="og:type" content="article" />
        <link rel="canonical" href={`https://mukeshrathore.com/blog/${blog.slug}`} />
      </Helmet>
      <Navbar />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className={`bg-gradient-to-br ${currentTheme.colors.primary} text-white py-16 px-6`}>
          <div className="max-w-4xl mx-auto">
            <Link to="/blog" className="text-white hover:text-purple-200 font-semibold mb-4 inline-block">
              ← Back to All Articles
            </Link>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-4 mb-4">
                <span className="bg-white text-purple-600 px-4 py-1 rounded-full text-sm font-semibold">
                  {blog.category}
                </span>
                <span className="text-purple-200">{blog.date}</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                {blog.title}
              </h1>
              <p className="text-xl text-purple-200">
                {blog.excerpt}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 px-6 bg-white">
          <div className="max-w-4xl mx-auto">
            {loading ? (
              <div className="text-center py-20">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-full mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6 mb-4"></div>
                </div>
              </div>
            ) : (
              <motion.article
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: convertMarkdownToHTML(content) }}
              />
            )}

            {/* CTA Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl p-8 text-center text-white"
            >
              <h3 className="text-3xl font-bold mb-4">Ready to Start Your Music Journey?</h3>
              <p className="text-xl mb-6 text-purple-100">
                Join Indie Music Creators and learn to create, produce, and launch professional original music
              </p>
              <button
                onClick={() => window.open('/zoom', '_blank')}
                className="bg-white text-purple-600 hover:bg-purple-50 font-bold py-4 px-8 rounded-lg text-lg transition-all transform hover:scale-105 shadow-xl"
              >
                Explore the Community →
              </button>
            </motion.div>

            {/* Back to Blog */}
            <div className="text-center mt-12">
              <Link
                to="/blog"
                className="text-purple-600 hover:text-purple-700 font-semibold text-lg"
              >
                ← Back to All Articles
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPost;
